/* eslint-disable @typescript-eslint/no-explicit-any */
/**
 * Subscription & Address API endpoints.
 */
import axios from 'axios';
import { getToken } from './api';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:8000/api/v1';

const authHeaders = () => ({
  headers: { Authorization: `Bearer ${getToken()}` }
});

// Subscription APIs
export const subscriptionApi = {
  getTiers: (mealFrequency: string = 'full') =>
    axios.get(`${API_BASE}/subscriptions/tiers`, { ...authHeaders(), params: { meal_frequency: mealFrequency } }),

  create: (data: {
    tier: string;
    delivery_address_id: number;
    delivery_slot?: string;
    duration?: string;
    meal_frequency?: string;
    light_meal_slot?: string;
    promo_code?: string;
  }) =>
    axios.post(`${API_BASE}/subscriptions/`, data, authHeaders()),

  getActive: () =>
    axios.get(`${API_BASE}/subscriptions/active`, authHeaders()),

  list: (skip = 0, limit = 20) =>
    axios.get(`${API_BASE}/subscriptions/`, { ...authHeaders(), params: { skip, limit } }),

  getRenewalInvite: () =>
    axios.get(`${API_BASE}/subscriptions/renewal-invite`, authHeaders()),

  getMealUsage: () =>
    axios.get(`${API_BASE}/subscriptions/active/meal-usage`, authHeaders()),

  getById: (id: number) =>
    axios.get(`${API_BASE}/subscriptions/${id}`, authHeaders()),

  pausePreview: (id: number, data: { pause_until: string; reason?: string }) =>
    axios.post(`${API_BASE}/subscriptions/${id}/pause-preview`, data, authHeaders()),

  pauseFinancial: (id: number, data: { pause_until: string; reason?: string }) =>
    axios.post(`${API_BASE}/subscriptions/${id}/pause-financial`, data, authHeaders()),

  pause: (id: number, data: { pause_until: string; reason?: string }) =>
    axios.post(`${API_BASE}/subscriptions/${id}/pause`, data, authHeaders()),

  resumeEarly: (id: number) =>
    axios.post(`${API_BASE}/subscriptions/${id}/pause/resume-early`, {}, authHeaders()),

  getPauseStatus: (id: number) =>
    axios.get(`${API_BASE}/subscriptions/${id}/pause/status`, authHeaders()),

  resume: (id: number) =>
    axios.post(`${API_BASE}/subscriptions/${id}/resume`, {}, authHeaders()),

  cancel: (id: number, data: { reason?: string; cancel_at_cycle_end?: boolean }) =>
    axios.post(`${API_BASE}/subscriptions/${id}/cancel`, data, authHeaders()),

  validatePromo: (data: { code: string; tier: string }) =>
    axios.post(`${API_BASE}/subscriptions/promo/validate`, data, authHeaders()),
};

// Address APIs
export const addressApi = {
  list: () =>
    axios.get(`${API_BASE}/addresses/`, authHeaders()),

  create: (data: any) =>
    axios.post(`${API_BASE}/addresses/`, data, authHeaders()),

  getById: (id: number) =>
    axios.get(`${API_BASE}/addresses/${id}`, authHeaders()),

  update: (id: number, data: any) =>
    axios.put(`${API_BASE}/addresses/${id}`, data, authHeaders()),

  delete: (id: number) =>
    axios.delete(`${API_BASE}/addresses/${id}`, authHeaders()),

  // Backend expects pincode (and optional lat/lng) as query params.
  checkServiceability: (pincode: string, latitude?: number | null, longitude?: number | null) => {
    const params = new URLSearchParams({ pincode });
    if (latitude != null) params.append('latitude', String(latitude));
    if (longitude != null) params.append('longitude', String(longitude));
    return axios.post(
      `${API_BASE}/addresses/check-serviceability?${params.toString()}`,
      {},
      authHeaders()
    );
  },
};

// Payment APIs
export const paymentApi = {
  createSubscriptionOrder: (subscriptionId: number) =>
    axios.post(`${API_BASE}/payments/create-subscription-order`, { subscription_id: subscriptionId }, authHeaders()),

  verify: (data: {
    subscription_id?: number;
    razorpay_subscription_id?: string;
    razorpay_order_id?: string;
    razorpay_payment_id: string;
    razorpay_signature: string;
  }) =>
    axios.post(`${API_BASE}/payments/verify`, data, authHeaders()),

  history: (page: number = 1, pageSize: number = 20) =>
    axios.get(`${API_BASE}/payments/history?page=${page}&page_size=${pageSize}`, authHeaders()),
};
